import React from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import TabbedAdminListPage, { TabbedAdminListPageTab } from 'hooks/TabbedAdminListPage';
import type { AdminListPageConfig, CustomModalDef } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { AICriteriaSet } from 'models/flexible/AICriteriaSet';
import { PERMISSIONS } from 'models/flexible/Profile';
import { aiCriteriaSetsQueryKeys, deleteAICriteriaSet, indexAICriteriaSets } from 'services/api/admin/aiCriteriaSets';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import CopyIcon from 'svg/CopyIcon';
import DeactivateIcon from 'svg/DeactivateIcon';
import PencilIcon from 'svg/PencilIcon';

import { buildPromptListConfig } from '../shared/promptListConfig';

// ---------------------------------------------------------------------------
// Admission Prompts tab config
// ---------------------------------------------------------------------------

const admissionPromptsConfig = buildPromptListConfig({
  targetClassname: 'Review::Admission',
  title: 'Admissions Prompts',
  addButtonLabel: 'Add Admission Prompt',
  archiveObjType: 'Admission Prompt',
  promptLabel: 'Admission',
  addPath: '/admin/admissions/prompts/new',
  editPath: (id) => `/admin/admissions/prompts/${id}/edit`,
  copyPath: (id) => `/admin/admissions/prompts/${id}/edit?copy=true`,
  defaultSort: 'updatedAt desc',
  clientFilterQueryKey: 'admissionPrompts',
});

// ---------------------------------------------------------------------------
// Admission Criteria tab config
// ---------------------------------------------------------------------------

const CRITERIA_INCLUDES = ['scopeable', 'client'] as const;

const admissionCriteriaConfig: AdminListPageConfig<AICriteriaSet> = {
  title: 'Admission Criteria',
  queryKey: aiCriteriaSetsQueryKeys.index(),
  fetchFn: (params, signal) => indexAICriteriaSets(params, signal, [...CRITERIA_INCLUDES]),
  includes: CRITERIA_INCLUDES,
  fetchParams: { defaultSort: 'updatedAt desc' },
  showResultCount: true,

  addButton: {
    label: 'Add Admission Criteria',
    permission: 'adminAICriteriaCreate',
    path: '/admin/admissions/criteria/new',
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'criteriaSets', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
    {
      key: 'scopeableId',
      label: 'Group',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminGroups', 'criteriaSets', 'filter'],
        fetchFn: (params: any, signal: any) => adminIndexGroups(params, signal, ['groupType']),
        params: { sortBy: 'name asc', isProvider: true },
      },
      getOptionLabel: (o: any) => (o?.groupType?.displayName ? `${o.name} (${o.groupType.displayName})` : o?.name),
      getOptionValue: (o: any) => o.id,
    },
  ],

  columns: [
    {
      id: 'client',
      header: 'Client',
      render: (row) => <BoldText>{row.client?.name || '\u2014'}</BoldText>,
    },
    {
      id: 'scopeable',
      header: 'Group',
      render: (row) => row.scopeable?.name || '\u2014',
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      render: (row) => (row.updatedAt ? simpleDateAtTime(row.updatedAt) : '\u2014'),
    },
  ],

  rowActions: [
    {
      label: 'Edit Criteria',
      icon: <PencilIcon width={18} height={18} />,
      permission: PERMISSIONS.adminAICriteriaEdit,
      onClick: (row, { navigate }) => navigate(`/admin/admissions/criteria/${row.id}/edit`),
    },
    {
      label: 'Duplicate Criteria',
      icon: <CopyIcon width={18} height={18} />,
      permission: PERMISSIONS.adminAICriteriaCreate,
      onClick: (row, { navigate }) => navigate(`/admin/admissions/criteria/${row.id}/edit?copy=true`),
    },
    {
      label: 'Archive Criteria',
      icon: <DeactivateIcon width={18} height={18} style={{ color: colors.accentRed }} />,
      danger: true,
      dividerBefore: true,
      visible: (row, ctx) => !row.archived && ctx.profile.has(PERMISSIONS.adminAICriteriaDelete),
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],
  customModals: {
    archive: {
      render: (row, show, onClose, ctx) => (
        <StandardModal title='Archive Criteria?' colorTheme={ColorThemeName.Danger} onCancel={onClose}>
          <ModalContent>
            <BodySmall>
              <strong>Are you sure you wish to archive this criteria?</strong> Once archived, it will no longer be used
              for {row.scopeable?.name} admissions summary.
            </BodySmall>
          </ModalContent>
          <Actions>
            <Button variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={async () => {
                await deleteAICriteriaSet(row.id);
                onClose();
                ctx.toast('Admission criteria was successfully archived');
                ctx.refetch();
              }}>
              Archive Criteria
            </Button>
          </Actions>
        </StandardModal>
      ),
    },
  } as Record<string, CustomModalDef<AICriteriaSet>>,
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const tabs: TabbedAdminListPageTab[] = [
  {
    label: 'Admission Prompts',
    path: '/admin/admissions/prompts',
    permission: 'adminAIPromptView',
    config: admissionPromptsConfig,
  },
  {
    label: 'Admission Criteria',
    path: '/admin/admissions/criteria',
    permission: 'adminAICriteriaView',
    config: admissionCriteriaConfig,
  },
];

export default function Admissions() {
  return <TabbedAdminListPage pageTitle='Admissions' tabs={tabs} />;
}

const BoldText = styled.span`
  font-weight: 700;
`;
